import { prisma } from "@/lib/prisma";
import { cache } from "react";
import { unstable_cache } from "next/cache";

// ─── Default values (fallbacks when DB has no override) ───

export interface TeamMember {
  name: string;
  title: string;
  bio: string;
}

// Categories of admin SMS notifications. Each list holds zero or more
// phone numbers — every phone gets a separate SMS for events in that
// category. A category with an empty list falls back to a legacy
// single-phone field (errorAlertPhone / supportPhone / etc.) so we
// don't go silent if the admin hasn't configured the new lists.
export interface NotificationRecipientLists {
  errorAlerts: string[];        // dev errors (logError fingerprints)
  chatMonitor: string[];        // chat new conversations + resumes
  newOrders: string[];          // orders placed, payment failed, refunds
  newAppointments: string[];    // new appointments, reschedules, cancels
  servicePickups: string[];     // service & repair pickups, Uber dispatch
  phoneCalls: string[];         // every inbound call (CallLog created)
}

// Voice agent (Twilio ConversationRelay) TTS settings. Stored as a
// pair so the IVR can build the right TwiML attributes:
//   <ConversationRelay ttsProvider="<provider>" voice="<voice>" ... />
// Three providers are supported by ConversationRelay; pricing differs
// by ~10x between cheapest and premium. Curated catalog of voice IDs
// lives in src/lib/voice-agent-catalog.ts.
export interface VoiceAgentConfig {
  ttsProvider: "Amazon" | "Google" | "ElevenLabs" | "OpenAI";
  /**
   * Provider-specific voice identifier:
   * - Amazon: "Polly.Joanna-Neural", "Polly.Ruth-Generative", etc.
   * - Google: "en-US-Neural2-F", "en-US-Chirp3-HD-Aoede", etc.
   * - ElevenLabs: 21-char ID, e.g. "EXAVITQu4vr4xnSDxMaC" (Sarah).
   *
   * Only honored when engine = "conversation_relay". The OpenAI
   * Realtime engine has its own voice catalog (alloy / echo / shimmer).
   */
  voice: string;
  /**
   * Voice-AI engine.
   *   conversation_relay (default) — Twilio ConversationRelay does
   *     STT, sends text to our Anthropic Claude relay, our relay
   *     streams text back, Twilio's TTS reads it. Cheapest, has the
   *     STT-fragmentation issue.
   *   openai_realtime — Twilio Media Streams pipes audio to OpenAI's
   *     speech-to-speech model (gpt-4o-realtime). Lower latency, no
   *     STT chunking, model "hears" tone. ~3-6× cost per minute.
   *
   * Switching takes effect on the next inbound Press-3 call (no
   * deploy needed; voice-ivr reads SiteConfig at TwiML build time).
   */
  engine?: "conversation_relay" | "openai_realtime";
}

// Permanent woven/satin label template. Coordinates are in millimetres
// from the TOP-LEFT of the label (so y=0 is the top edge, growing
// downward). The PDF generator flips Y at render time since pdf-lib
// uses bottom-left origin. Anchor controls which point of the text
// box anchors at (x,y): "center" = horizontal center, "left" = left
// edge of text, "right" = right edge.
export interface WigLabelTemplate {
  widthMm: number;
  heightMm: number;
  bgColor: string;          // hex incl. # — e.g. "#fdfaf3"
  // Wordmark (top). Either inline text or an uploaded image URL.
  // When kind === "image", `text` is ignored.
  logo: {
    kind: "text" | "image";
    text: string;
    fontFamily: "playfair" | "helvetica" | "times-italic";
    fontSizeMm: number;
    color: string;
    imageUrl?: string;
    imageWidthMm?: number;  // image width; height auto-scaled
    x: number;
    y: number;
    anchor: "center" | "left" | "right";
  };
  // Decorative gold rule under the logo.
  divider: {
    show: boolean;
    y: number;
    widthMm: number;
    color: string;
  };
  // QR code centered on the label by default. Always present (the
  // whole point of the tag is the QR linking to /w/{barcodeId}).
  qr: {
    sizeMm: number;
    x: number;
    y: number;
    color: string;
  };
  // Serial / barcodeId text (e.g. "WIG-000042") shown below the QR.
  serial: {
    show: boolean;
    fontFamily: "playfair" | "helvetica" | "times-italic";
    fontSizeMm: number;
    color: string;
    x: number;
    y: number;
    anchor: "center" | "left" | "right";
  };
  // Optional free-form text strings admin can position anywhere.
  customText: Array<{
    id: string;
    text: string;
    fontFamily: "playfair" | "helvetica" | "times-italic";
    fontSizeMm: number;
    color: string;
    x: number;
    y: number;
    anchor: "center" | "left" | "right";
  }>;
}

export interface SiteConfigData {
  // Business Info
  businessName: string;
  tagline: string;
  storeAddress: string;
  supportPhone: string;
  /**
   * Phone displayed for "Call us" buttons site-wide. Distinct from
   * supportPhone (used for SMS) so the toll-free can take voice
   * traffic while texts route to the regular line where the team
   * actually reads incoming SMS without webhook indirection. Falls
   * back to supportPhone when empty.
   */
  voicePhone: string;
  supportEmail: string;
  socialInstagram: string;
  socialFacebook: string;
  socialTiktok: string;

  // Shipping
  standardShippingCost: number;
  freeShippingThreshold: number;
  localDeliveryFee: number;
  deliveryAreaDescription: string;

  // ─── Ship-From Address (for carrier labels) ───
  // Structured version of storeAddress — used when creating shipping labels
  // via EasyPost / USPS / UPS / FedEx.
  shipFromName: string;
  shipFromCompany: string;
  shipFromStreet1: string;
  shipFromStreet2: string;
  shipFromCity: string;
  shipFromState: string; // 2-letter
  shipFromZip: string;
  shipFromCountry: string; // ISO 3166-1 alpha-2, defaults to "US"
  shipFromPhone: string;

  // Pricing
  builderBasePrice: number;
  serviceDepositAmount: number;
  taxRate: number;
  giftCardPresets: number[];
  giftCardExpiryYears: number; // default 1
  maxQuantityPerCartItem: number; // default 10

  // Appointment / cancellation rules
  appointmentCancelRefundWindowDays: number; // default 2 (days before appt to still get refund)

  // Abandoned cart hard-stop
  abandonedCartMaxDays: number; // default 6 — past this, cron gives up sending

  // Returns
  returnInsuranceThreshold: number; // default 100 — recommend insurance above this

  // AI wig match caps
  wigMatchWeeklyCap: number; // default 5 photos/week/user (logged-in)
  wigMatchGuestHourlyCap: number; // default 20/hr/IP for guests

  // Team Members
  teamMembers: TeamMember[];

  // Customer Notifications
  notifyOnShipped: boolean;
  notifyOnDelivered: boolean;
  notifyOnDelayed: boolean;

  // ─── SMS quiet hours ───
  // Customer-facing sendSMS + sendWhatsApp skip sends inside this
  // window (email fallback still fires). Urgent calls with
  // { urgent: true } bypass — admin alerts, campaigns, OTP/reset codes.
  // Hours are 0-23 in the chosen IANA timezone; window wraps past
  // midnight when endHour < startHour (the normal case, e.g. 21→8).
  smsQuietHoursEnabled: boolean;
  smsQuietStartHour: number;  // 0-23, default 21 (9pm)
  smsQuietEndHour: number;    // 0-23, default 8 (8am)
  smsQuietTimezone: string;   // IANA, default "America/New_York"

  // ─── Error-tracking SMS alerts ───
  // Every NEW error fingerprint (first time we see this signature)
  // fires an urgent SMS to errorAlertPhone. Per-container 5/hour cap
  // so an outage doesn't blast the inbox. Recurring errors increment
  // ErrorLog.occurrences silently — only first-sightings text.
  errorAlertsEnabled: boolean;
  errorAlertPhone: string; // Defaults to dev/owner phone, NOT supportPhone

  // ─── Live-chat monitoring SMS ───
  // When ON, fires an urgent SMS on every NEW chat conversation (not
  // every message — only the first one) with a deep-link to the
  // admin inbox. Useful when an admin wants to jump in and monitor /
  // take over a session in real time. Phone defaults to
  // errorAlertPhone if not set.
  chatNewConvSmsEnabled: boolean;
  chatNewConvSmsPhone: string;

  // Per-category SMS recipient lists. Each list can hold any number
  // of phone numbers. When empty, the legacy single-phone fields
  // (errorAlertPhone / chatNewConvSmsPhone / supportPhone) act as
  // fallback so existing prod doesn't go silent on rollout.
  // Edit at /admin/settings → Notifications → "Who gets SMS for..."
  notificationRecipients: NotificationRecipientLists;

  // Permanent woven/satin label design template (sewn into every new
  // RW wig). Customizable at /admin/settings/wig-labels — controls
  // dimensions, font, color, logo (text vs uploaded image), and
  // element positions. Persisted as JSON; consumed by
  // /api/admin/wig-tag-export when generating the vendor PDF.
  labelTemplate: WigLabelTemplate;

  // Voice agent (Twilio ConversationRelay) — TTS provider + voice ID
  // used when a caller picks Press 3 in the IVR. Read at TwiML build
  // time in /api/twilio/webhook/voice-ivr. Edit at
  // /admin/settings/voice-agent. ElevenLabs voices require an
  // ElevenLabs API key registered in the Twilio Console under
  // ConversationRelay providers — until that's wired in, leave on
  // Amazon or Google.
  voiceAgent: VoiceAgentConfig;

  // Free-form admin-editable knowledge for the AI chat + voice agent.
  // Plain text, gets injected verbatim into the system prompt's
  // knowledge base section. Use for: temporary notes ("Closed May
  // 10-15 for Yom Tov"), promo announcements ("SPRING25 = 25% off
  // through May 31"), policy clarifications ("We don't do braids"),
  // anything Rivky wants the agent to know without filing a code
  // change. Empty by default. Edit at /admin/settings → "Agent
  // knowledge".
  agentKnowledge: string;

  // Marketing popups
  newsletterPopupEnabled: boolean;
  exitIntentPopupEnabled: boolean;
  newsletterPopupDiscountPercent: number; // % off shown in newsletter signup popup
  newsletterPopupDelaySeconds: number;

  // Guided tour (react-joyride overlay) — default off. Turn on once the
  // react-joyride integration is stable; previously caused customer-site
  // crashes on every page when enabled unconditionally.
  guidedTourEnabled: boolean;

  // ─── Promotion settings — every value below is admin-editable ───

  // Birthday emails
  birthdayEmailsEnabled: boolean;
  birthdayDiscountPercent: number; // 0-100
  birthdayCouponValidDays: number; // how long the birthday coupon stays valid

  // Abandoned cart sequence
  abandonedCartEnabled: boolean;
  abandonedCartStep1DelayHours: number; // time after idle before first email (default 1)
  abandonedCartStep2DelayHours: number; // default 24
  abandonedCartStep3DelayHours: number; // default 72
  // Per-step enabled toggles. Lets admin run e.g. "only reminders, no
  // discount" by keeping step 1 on and turning steps 2+3 off, or skip
  // an intermediate step. When OFF, the cron simply doesn't fire that
  // step's email (no coupon generated either).
  abandonedCartStep2Enabled: boolean;
  abandonedCartStep3Enabled: boolean;
  // Per-step "offer a discount" toggles. When OFF, the step still
  // sends its reminder email but skips the coupon entirely (no {{couponCode}}
  // in the email). When ON, a one-use coupon at abandonedCartStepNDiscountPercent
  // is generated. Lets admin decide "nudge only" vs "nudge + offer".
  abandonedCartStep2DiscountEnabled: boolean;
  abandonedCartStep3DiscountEnabled: boolean;
  abandonedCartStep2DiscountPercent: number; // default 5
  abandonedCartStep3DiscountPercent: number; // default 10

  // First-order discount (auto-applied at first checkout if firstOrderDiscountUsed is false)
  firstOrderDiscountEnabled: boolean;
  firstOrderDiscountPercent: number;

  // Gift wrap
  giftWrapEnabled: boolean;
  giftWrapPrice: number; // per item

  // Free shipping threshold (already has freeShippingThreshold above; kept here for consistency)
  // Free gift with purchase (referenced by DiscountRule.freeGiftProductId; this is the master toggle)
  freeGiftEnabled: boolean;

  // Wig maintenance reminders (cron-based)
  maintenanceRemindersEnabled: boolean;
  maintenanceReminderIntervalDays: number; // days since last service before sending reminder
  maintenanceReminderCooldownDays: number; // minimum days between reminders

  // ─── Referral program settings ───
  referralEnabled: boolean;
  referrerRewardAmount: number; // credit the referrer gets ($ OR points)
  refereeDiscountAmount: number; // discount the referee gets ($)
  referralMinPurchase: number; // minimum purchase for referral to count
  referralMessage: string; // customizable share message text
  // Owner 2026-04-15 — admin can switch rewards between store credit
  // (default) and loyalty points. "points" uses `referrerRewardAmount`
  // as a raw point count (100 points ≈ $1). "dollars" creates a coupon.
  referralRewardType: "dollars" | "points";
  /**
   * Cap on the total OUTSTANDING (unredeemed) referral credit a single
   * referrer can hold at any time, in dollars. Computed by summing the
   * $value of unused REFR-XXXXXXXX coupons issued to them via
   * awardReferralReward. When a new qualifying referee purchase would
   * push the referrer past this cap, the reward is suppressed and a
   * "you hit the cap — spend it to keep earning" email is sent instead.
   * Once the referrer redeems a coupon (balance drops), future
   * referrals can earn again. Default $300. Set to 0 to disable the cap.
   */
  maxReferralCreditBalance: number;

  // ─── Reseller / Sheitel Macher settings ───
  resellerRegistrationFee: number; // one-time fee to open reseller account
  resellerDepositPercent: number; // % deposit on order (balance on pickup)
  // Consignment-sale deposit config. When a reseller reports a sale
  // of a consigned wig, this controls how much deposit we invoice them
  // upfront (before touchup). Per-reseller override lives on the User
  // row — this is the platform-wide default.
  //   "percent" → `consignmentDepositPercent` of reseller wholesale price
  //   "fixed"   → `consignmentDepositFixed` flat amount
  consignmentDepositMode: "percent" | "fixed";
  consignmentDepositPercent: number; // 0-100
  consignmentDepositFixed: number;   // dollars
  // If true, wigs currently on consignment with a reseller still
  // appear as in-stock on the public site (shop-from-anywhere model).
  // Customer orders trigger a ConsignmentRecall so the reseller ships
  // the wig back to the salon, then admin ships to customer.
  consignmentStockVisibleToPublic: boolean;

  // Booking consolidation feature flag (Phase 2 — wiki/plans/booking-
  // consolidation-2026-04-28.md). When true, /consultation redirects to
  // /book and the unified 5-step flow is rendered. When false (default
  // until launch-tested), legacy /book + /consultation flows render
  // independently. Admin flips this from /admin/settings → Booking.
  unifiedBookingFlow: boolean;

  resellerDropoffSchedule: string; // human-readable drop-off/pickup hours
  resellerColorServices: ResellerService[]; // wholesale color service pricing
  resellerRepairServices: ResellerService[]; // wholesale repair service pricing
  // Retail pricing — admin quick-picks these on walk-in / appointment
  // detail when setting serviceCost. Shape mirrors the reseller lists.
  retailColorServices: ResellerService[];
  retailRepairServices: ResellerService[];
  /**
   * Sub-tiers under "New wig cut / recut" (the parent SERVICES.id is
   * `restyle`). Customer sees these as expanded options when they tap
   * the cut card on /book. Each row: name + price (or range) +
   * description shown as the explainer copy.
   */
  retailCutServices: ResellerService[];
  retailApptServices: ResellerService[]; // wash-set, new-wig consult, etc.
  // Sub-tiers for the remaining master service categories. Every
  // parent service can now have its own sub-catalog expanding under
  // it on /book — was only color/cuts/repair before.
  retailWashSetServices: ResellerService[];
  retailDeepCleanServices: ResellerService[]; // keratin / deep-clean tiers
  retailResizeServices: ResellerService[];

  // ─── Analytics access control ───
  analyticsEmails: string[]; // email addresses allowed to view /admin/analytics

  // ─── Loyalty redemption freeze windows (Rosenberg 2026-04-15) ───
  // Points can be used any time EXCEPT these ranges. Typical use:
  // block the month before big holidays when the salon is already
  // overbooked. Admin edits in /admin/settings.
  loyaltyFreezeRanges: LoyaltyFreezeRange[];
  // Owner 2026-04-15 — send an expiry reminder N days before the
  // user's earliest points expire. 60 = 2 months. Not hardcoded.
  loyaltyExpiryReminderDays: number;

  // ─── Loyalty earn / redeem rates (admin-editable, was hardcoded) ───
  // Points = floor(orderAmount * loyaltyPointsPerDollar) × tier multiplier.
  // Redemption: minimum loyaltyMinRedeemPoints to redeem; rate is
  // loyaltyDollarsPer100Points dollars per 100 points (so 1 = "100 pts =
  // $1", 2 = "100 pts = $2"). Expiry is loyaltyPointsExpiryMonths after
  // earning. Tiers below are the lifetime-spend thresholds + earn
  // multipliers — bronze stays at 0 always; gaps re-sort on save.
  loyaltyPointsPerDollar: number;
  loyaltyMinRedeemPoints: number;
  loyaltyDollarsPer100Points: number;
  loyaltyPointsExpiryMonths: number;
  loyaltyTiers: Array<{
    name: string;       // bronze | silver | gold | platinum (free-form)
    minPoints: number;  // lower bound (inclusive); next tier's min is the upper bound
    multiplier: number; // earn multiplier, e.g. 1.25
  }>;

  // ─── Time-clock / payroll settings (all admin-editable) ───
  timeClockEnabled: boolean;                  // master toggle for the feature
  timeClockDefaultHourlyRate: number;         // used on user create when rate omitted
  timeClockOvertimeThresholdHoursPerWeek: number; // default 40
  timeClockOvertimeMultiplier: number;        // default 1.5 (time-and-a-half)
  timeClockAutoCloseAfterHours: number;       // default 14; cron force-closes open shifts older than this
  timeClockRequireGeofence: boolean;          // when true, clock-in blocked outside the fence
  timeClockGeofenceCenterLat: number | null;  // null = disabled
  timeClockGeofenceCenterLng: number | null;
  timeClockGeofenceRadiusMeters: number;      // default 150
  timeClockMaxShiftHoursWarning: number;      // default 10 — UI hints when a shift gets unusually long
  timeClockBreakAutoDeductAfterHours: number | null; // null = off; otherwise auto-deduct 30m unpaid after N hrs
  timeClockPtoEnabled: boolean;               // master toggle for the PTO feature (tab visibility + API)
  timeClockPtoAccrualPerHour: number;         // 0 = off; otherwise accrue N PTO hrs per hour worked
  timeClockPayoutMethods: string[];           // enabled payment methods (cash, check, venmo, zelle, paypal, other)
  timeClockAdminNotifyOnScan: boolean;        // ping admins on every clock-in/out

  // ─── Pay schedule ───
  // Drives pay-period computation + "next pay day" displays on both
  // admin and employee time-card pages.
  timeClockPayFrequency: "weekly" | "biweekly" | "semimonthly" | "monthly";
  // For weekly/biweekly: which day starts the week (0=Sun...6=Sat).
  // Monday (1) is the most common pay-period start.
  timeClockPayWeekStart: number;
  // Biweekly needs an anchor date so we know which week pair starts a
  // new period. Pick any Monday that begins a pay period; all subsequent
  // periods are computed off this anchor.
  timeClockPayBiweeklyAnchor: string; // ISO YYYY-MM-DD
  // Days after the period ends that payment is actually issued (e.g.
  // period ends Sunday, pay day is Friday = offset 5).
  timeClockPayDayOffsetDays: number;

  // ─── Employee handbook ───
  // URL to the handbook (Notion, PDF, Google Doc, etc.). Blank = no
  // acknowledgment required. Bumping version forces re-acknowledgment.
  employeeHandbookUrl: string;
  employeeHandbookVersion: number;

  // ─── Coming-Soon toggles ───
  // Per-section flags. When true, the section's page renders a
  // "Coming Soon" view and the corresponding nav link is hidden.
  // Replaces the prior hardcoded BUILDER_COMING_SOON constant.
  comingSoon: {
    wigs: boolean;
    builder: boolean;
    blog: boolean;
    about: boolean;
  };

  // ─── Build lifecycle (Phase 1+2+3, 2026-05-03) ───
  // Per-category default ETA range when a wig enters in_production.
  // Map keyed by Product.categorySlug ("wigs" = virgin, "processed-wigs",
  // "toppers"). Per-Product override fields exist on Product itself.
  buildDurationByCategory: Record<string, { minWeeks: number; maxWeeks: number }>;
  // Default deposit % for custom-wig orders + speculative-build claims.
  // Snapshot onto Order.depositPercent at checkout time so a later
  // change to this value doesn't retroactively reshape old orders.
  customWigDepositPercent: number; // 0-100
  // Heartbeat cron cadence — every N days since order date (or last
  // heartbeat) we send the customer a "where we are" SMS+email update.
  // Set to 0 to disable the cron entirely.
  buildHeartbeatDays: number;
  // Days post-ready when balance reminders fire. Last entry triggers
  // the "your hold is releasing" warning; one day past that, the wig
  // auto-releases (per balanceAutoRelistDays below).
  balanceReminderSchedule: number[];
  // Number of days post-ready an unpaid balance results in the wig
  // being released back to inventory + Order auto-cancelled with full
  // deposit refund.
  balanceAutoRelistDays: number;

  // ─── Per-stage refund policy (Phase 4, 2026-05-03) ───
  // Map keyed by BuildStage.internalLabel: refund % the customer gets
  // back if they cancel WHILE the asset's currentStage is at this key.
  // depositForfeited=true means even a partial refund excludes the
  // deposit. Used by /account/wigs/[barcode] cancel-build button +
  // /admin/orders/[id] cancel flow. Stages not in the map default to
  // 100% refund (i.e., before any production work was started).
  // Falls back to admin-judgment cancel when missing.
  customWigRefundPolicy: Record<string, { refundPct: number; depositForfeited: boolean }>;
  // Capacity gate for speculative-build storefront listings. When
  // count(WigAsset where lifecycleStatus=in_production AND
  // listedForSale=true) >= cap, the InProductionBuilds panel hides
  // unclaimed cards on PDPs (already-claimed customer builds keep
  // running). 0 = unlimited.
  maxConcurrentBuilds: number;
  // Spec-drift mid-build: when admin changes a customer-bound
  // WigAsset's name/description/spec while in_production, fire
  // customer notification with the diff + grace-cancel window.
  specDriftCancelWindowHours: number;
}

export interface LoyaltyFreezeRange {
  startDate: string; // ISO "YYYY-MM-DD" (inclusive)
  endDate: string;   // ISO "YYYY-MM-DD" (inclusive)
  label: string;     // e.g. "Before Pesach 2026"
}

export interface ResellerService {
  name: string;
  price: number; // in USD. If 0, shown as "Contact for quote"
  priceMax?: number; // if set, displayed as "$price – $priceMax"
  description?: string;
  /**
   * Payment-at-booking policy for retail rows (Manny #148):
   *   - "free"               → no card form at booking (default, current)
   *   - "request_with_skip"  → show card + a "Skip — pay at appointment" toggle
   *   - "require"            → mandatory card capture, no skip
   * Wholesale rows (resellerColorServices) ignore this and stay free.
   * Undefined = "free" for backward compat with existing config rows.
   */
  paymentMode?: "free" | "request_with_skip" | "require";
}

export const SITE_CONFIG_DEFAULTS: SiteConfigData = {
  // Business Info
  businessName: "Rivky Wigs",
  tagline: "Premium handcrafted wigs and accessories",
  storeAddress: "1935 Whitesville Rd, Toms River, NJ 08755",
  supportPhone: "(347) 452-3279",
  // Toll-free for voice — verified 2026-04-28. Customers call this;
  // the inbound voice webhook forwards to TWILIO_FORWARD_NUMBER
  // (Rivky's mobile) and records the call for AI summary.
  voicePhone: "(888) 907-9447",
  supportEmail: "info@rivkywigs.com",
  socialInstagram: "https://instagram.com/rivky_wigs",
  socialFacebook: "https://facebook.com/rwwigs",
  socialTiktok: "https://tiktok.com/@rwwigs",

  // Shipping
  standardShippingCost: 15.0,
  freeShippingThreshold: 500,
  localDeliveryFee: 12.99,
  deliveryAreaDescription: "Lakewood, Toms River & Jackson NJ",

  // Ship-From defaults — matches storeAddress
  shipFromName: "Rivky Wigs",
  shipFromCompany: "Rivky Wigs",
  shipFromStreet1: "1935 Whitesville Rd",
  shipFromStreet2: "",
  shipFromCity: "Toms River",
  shipFromState: "NJ",
  shipFromZip: "08755",
  shipFromCountry: "US",
  shipFromPhone: "(347) 452-3279",

  // Pricing
  builderBasePrice: 2500,
  serviceDepositAmount: 50,
  taxRate: 0.06625,
  giftCardPresets: [25, 50, 100, 250, 500],
  giftCardExpiryYears: 1,
  maxQuantityPerCartItem: 10,

  // Appointment / cancellation rules
  appointmentCancelRefundWindowDays: 2,

  // Abandoned cart hard-stop
  abandonedCartMaxDays: 6,

  // Returns
  returnInsuranceThreshold: 100,

  // AI wig match caps
  wigMatchWeeklyCap: 5,
  wigMatchGuestHourlyCap: 20,

  // Team Members
  teamMembers: [
    {
      name: "Rivky Rosenberg",
      title: "Founder & Creative Director",
      bio: "With years of experience in the luxury wig industry, Rivky founded Rivky Wigs on the belief that exceptional quality and personal attention should be the standard, not the exception.",
    },
    {
      name: "Rachel Cohen",
      title: "Master Wigmaker",
      bio: "Trained in European hair artistry, Rachel brings extraordinary precision and artistry to every wig she creates. Her hands have crafted thousands of pieces, and her passion for perfection grows stronger with each one.",
    },
    {
      name: "Miriam Levy",
      title: "Client Experience Director",
      bio: "Miriam ensures that every client feels heard, supported, and genuinely cared for -- from the first conversation to the moment they find their perfect piece and beyond.",
    },
  ],

  // Customer Notifications (defaults: all on)
  notifyOnShipped: true,
  notifyOnDelivered: true,
  notifyOnDelayed: true,

  // SMS quiet hours — on by default; 9pm-8am Eastern (NJ studio zone)
  smsQuietHoursEnabled: true,
  smsQuietStartHour: 21,
  smsQuietEndHour: 8,
  smsQuietTimezone: "America/New_York",

  // Error-tracking SMS alerts — on by default, dev/owner phone
  errorAlertsEnabled: true,
  errorAlertPhone: "(516) 236-7397",
  chatNewConvSmsEnabled: true,
  chatNewConvSmsPhone: "",

  // Default lists are empty — the resolver falls back to the legacy
  // single-phone fields above for first-run behavior. Admin populates
  // these lists at /admin/settings → Notifications.
  notificationRecipients: {
    errorAlerts: [],
    chatMonitor: [],
    newOrders: [],
    newAppointments: [],
    servicePickups: [],
    phoneCalls: [],
  },

  // Default label = 30×50mm satin (Wunderlabel premium woven), cream
  // background, "Rivky Wigs" italic-serif wordmark, gold rule, centered
  // QR, bold-mono serial below. Matches what the prior hardcoded
  // generator emitted before the designer existed.
  labelTemplate: {
    widthMm: 30,
    heightMm: 50,
    bgColor: "#fdfaf3", // brand cream
    logo: {
      kind: "text",
      text: "Rivky Wigs",
      fontFamily: "times-italic",
      fontSizeMm: 4,
      color: "#1b1817",
      x: 15,        // center of 30mm label
      y: 6,         // 6mm from top
      anchor: "center",
    },
    divider: {
      show: true,
      y: 10,
      widthMm: 8,
      color: "#a58c76",
    },
    qr: {
      sizeMm: 18,
      x: 15,        // center horizontally
      y: 22,        // ~middle of label
      color: "#000000",
    },
    serial: {
      show: true,
      fontFamily: "helvetica",
      fontSizeMm: 3,
      color: "#1b1817",
      x: 15,
      y: 46,
      anchor: "center",
    },
    customText: [],
  },

  // Voice agent default — Polly Joanna-Neural. Cheapest tier
  // (~$0.005/min effective) with solid prosody support. Admin can
  // upgrade to Polly-Generative, Google Chirp3-HD, or ElevenLabs at
  // /admin/settings/voice-agent.
  voiceAgent: {
    ttsProvider: "Amazon",
    voice: "Polly.Joanna-Neural",
    engine: "conversation_relay",
  },

  agentKnowledge: "",

  // Marketing popups (default: off — admin opts in)
  newsletterPopupEnabled: false,
  exitIntentPopupEnabled: false,
  newsletterPopupDiscountPercent: 10,
  newsletterPopupDelaySeconds: 30,

  // Guided tour (default off until react-joyride integration is stable)
  guidedTourEnabled: false,

  // ─── Promotion settings (defaults — admin can override any) ───
  birthdayEmailsEnabled: true,
  birthdayDiscountPercent: 15,
  birthdayCouponValidDays: 30,

  abandonedCartEnabled: true,
  abandonedCartStep1DelayHours: 1,
  abandonedCartStep2DelayHours: 24,
  abandonedCartStep3DelayHours: 72,
  abandonedCartStep2Enabled: true,
  abandonedCartStep3Enabled: true,
  abandonedCartStep2DiscountEnabled: true,
  abandonedCartStep3DiscountEnabled: true,
  abandonedCartStep2DiscountPercent: 5,
  abandonedCartStep3DiscountPercent: 10,

  firstOrderDiscountEnabled: false,
  firstOrderDiscountPercent: 10,

  giftWrapEnabled: true,
  giftWrapPrice: 5,

  freeGiftEnabled: false,

  // Wig maintenance reminders
  maintenanceRemindersEnabled: true,
  maintenanceReminderIntervalDays: 28,
  maintenanceReminderCooldownDays: 14,

  // ─── Referral defaults ───
  referralEnabled: true,
  referrerRewardAmount: 150,
  refereeDiscountAmount: 100,
  referralMinPurchase: 0,
  referralMessage: "Love your new wig? Share the love! Give your friend $100 off their first purchase and earn $150 in credit when they buy.",
  referralRewardType: "dollars", // admin can flip to "points" in settings
  maxReferralCreditBalance: 300,

  // ─── Reseller defaults ───
  resellerRegistrationFee: 2000,
  resellerDepositPercent: 50,
  // Consignment deposit defaults — admin can override per-reseller on
  // the user detail page's Billing tab.
  consignmentDepositMode: "percent",
  consignmentDepositPercent: 30,
  consignmentDepositFixed: 200,
  consignmentStockVisibleToPublic: true,
  unifiedBookingFlow: false,
  resellerDropoffSchedule: "Monday – Friday: 9:00 – 10:00 AM\nSunday: 1:00 – 4:00 PM",

  resellerColorServices: [
    { name: "Roots", price: 140 },
    { name: "Low Lights", price: 140 },
    { name: "Roots and Low Lights", price: 230 },
    { name: "Highlights", price: 200 },
    { name: "Full Color (Blonde to Dark)", price: 300 },
    { name: "Full Color (Dark to Blonde)", price: 400 },
    { name: "Orange / Expedite to Nice Color", price: 340 },
  ],

  resellerRepairServices: [
    { name: "Basic Hairline", price: 200 },
    { name: "Multi-Directional Hairline", price: 280 },
    { name: "Hairline Add-In / Balding Spots", price: 100, description: "Starting at" },
    { name: "Baby Hair (Non-Lace)", price: 100 },
    { name: "Add Lace Front", price: 500, priceMax: 1000 },
    { name: "Lace Extension", price: 300, priceMax: 600 },
    { name: "Lining", price: 50 },
    { name: "Take In Cap", price: 100 },
    { name: "Tighten Lace", price: 25 },
    { name: "Close Lace + Baby Hair", price: 200 },
    { name: "Remove Ear Tabs", price: 80 },
    { name: "Remove Weft", price: 20, description: "Per weft" },
    { name: "Add Weft", price: 40, description: "Per weft" },
    { name: "Cap Transfer", price: 310 },
  ],

  // Retail pricing — shown to admin on walk-in / appointment detail as
  // quick-pick options when setting the service cost. Edit anytime at
  // /admin/site-config. Customer sees only the final assigned cost.
  retailColorServices: [
    { name: "Roots", price: 200, description: "Cover the new growth at the part — keeps your color seamless between full appointments." },
    { name: "Low Lights", price: 200, description: "Add deeper tones woven through for dimension and contrast." },
    { name: "Roots and Low Lights", price: 320, description: "Combine a roots refresh with low-light dimension in one visit." },
    { name: "Highlights", price: 280, description: "Brighter face-framing or partial weave to lift overall tone." },
    { name: "Full Color (Blonde to Dark)", price: 400, description: "Whole-head color change going darker — usually a single application." },
    { name: "Full Color (Dark to Blonde)", price: 550, description: "Whole-head lift from dark to blonde — multi-step process for healthy hair." },
  ],
  retailRepairServices: [
    { name: "Basic Hairline", price: 280, description: "Reinforce or rebuild the hairline edge — stops pulling and re-shapes the front." },
    { name: "Multi-Directional Hairline", price: 380, description: "Hairline rebuilt with hairs flowing in different directions for the most natural look." },
    { name: "Hairline Add-In", price: 150, description: "Starting at — fill in thinned spots along the hairline." },
    { name: "Baby Hair (Non-Lace)", price: 150, description: "Add wispy baby hairs around the edges for a softer, more natural finish." },
    { name: "Add Lace Front", price: 700, priceMax: 1200, description: "Convert a non-lace cap to a lace front — most invisible hairline available." },
    { name: "Tighten Lace", price: 40, description: "Quick fix for loose lace that's lifted from the cap." },
    { name: "Close Lace + Baby Hair", price: 280, description: "Close stretched lace and add baby hairs — full hairline refresh." },
  ],
  // Sub-tiers under "New wig cut / recut" (parent service: restyle).
  // Industry-standard 3-tier defaults — Rivky can edit / add / remove
  // at /admin/settings → Retail service pricing. Descriptions explain
  // what's included so customers can self-select the right tier.
  retailCutServices: [
    { name: "Recut / Touch-Up", price: 80, description: "Clean up the ends + restore the original shape — perfect for a wig that's grown out." },
    { name: "Layered Cut", price: 120, description: "Full restyle with face-framing layers and shaping for movement." },
    { name: "Custom Design Cut", price: 200, description: "Full reshape from scratch — bangs, side-sweep, signature look. Includes a consultation on style." },
  ],
  retailApptServices: [
    { name: "Wash & Set", price: 80 },
    { name: "New wig cut / recut", price: 120 },
    { name: "Keratin Treatment", price: 150 },
    { name: "Resize", price: 100 },
    { name: "New Wig Consultation", price: 0, description: "Complimentary" },
  ],
  // Sub-tiers under Wash & Set. Rivky's actual price card — distinguishes
  // a routine wash from an event-day style or a same-day reset.
  retailWashSetServices: [
    { name: "Wash and Set", price: 65, description: "A full salon shampoo, conditioning, and blow-out or set on your wig — the everyday refresh that keeps colors vibrant and the cap clean." },
    { name: "Wedding Style", price: 150, priceMax: 200, description: "Event-ready styling for weddings and special occasions. Includes a wash + custom set, optional curls or up-styling, and finishing touches so the wig holds shape through the day." },
    { name: "Reset", price: 50, description: "A quick reset between wears — minor restyle, smoothing, and refresh of the shape without a full wash. Ideal when you wore it the night before and need it ready again." },
  ],
  // Sub-tiers under Keratin Treatment / Deep Clean. Empty by default
  // — Rivky can populate in admin if she wants to offer a full vs
  // light treatment.
  retailDeepCleanServices: [],
  // Sub-tiers under Resize. Empty by default — admin-editable.
  retailResizeServices: [],

  // ─── Analytics defaults ───
  analyticsEmails: ["admin@rivkywigs.com", "shmuelsokol@yahoo.com", "qa-admin@rivkywigs.com"],

  // ─── Loyalty freeze — pre-populated with the 5 busy-season windows
  // before each major Jewish holiday 2026-2028. Each freeze starts
  // 30 days before the holiday's first day and ends the day before.
  // Admin edits in /admin/settings to add/remove/extend ranges.
  // Refresh yearly — hebcal auto-refresh is a follow-up. ─────────────
  loyaltyFreezeRanges: [
    // 2026 — only Rosh Hashana onward is still future from April 2026
    { startDate: "2026-08-13", endDate: "2026-09-11", label: "Before Rosh Hashana 2026" },
    { startDate: "2026-08-22", endDate: "2026-09-20", label: "Before Yom Kippur 2026" },
    { startDate: "2026-08-27", endDate: "2026-09-25", label: "Before Sukkos 2026" },
    // 2027
    { startDate: "2027-03-23", endDate: "2027-04-21", label: "Before Pesach 2027" },
    { startDate: "2027-05-12", endDate: "2027-06-10", label: "Before Shavuos 2027" },
    { startDate: "2027-09-02", endDate: "2027-10-01", label: "Before Rosh Hashana 2027" },
    { startDate: "2027-09-11", endDate: "2027-10-10", label: "Before Yom Kippur 2027" },
    { startDate: "2027-09-16", endDate: "2027-10-15", label: "Before Sukkos 2027" },
    // 2028
    { startDate: "2028-03-12", endDate: "2028-04-10", label: "Before Pesach 2028" },
    { startDate: "2028-05-01", endDate: "2028-05-30", label: "Before Shavuos 2028" },
    { startDate: "2028-08-21", endDate: "2028-09-19", label: "Before Rosh Hashana 2028" },
    { startDate: "2028-08-30", endDate: "2028-09-28", label: "Before Yom Kippur 2028" },
    { startDate: "2028-09-04", endDate: "2028-10-03", label: "Before Sukkos 2028" },
  ],
  loyaltyExpiryReminderDays: 60,

  // Loyalty earn / redeem rates — defaults match the prior hardcoded
  // constants so existing customers see no behavior change on the
  // SiteConfig migration.
  loyaltyPointsPerDollar: 1,
  loyaltyMinRedeemPoints: 100,
  loyaltyDollarsPer100Points: 1,
  loyaltyPointsExpiryMonths: 12,
  loyaltyTiers: [
    { name: "bronze", minPoints: 0, multiplier: 1 },
    { name: "silver", minPoints: 500, multiplier: 1.25 },
    { name: "gold", minPoints: 2000, multiplier: 1.5 },
    { name: "platinum", minPoints: 10000, multiplier: 2 },
  ],

  // Time-clock defaults
  timeClockEnabled: true,
  timeClockDefaultHourlyRate: 0,
  timeClockOvertimeThresholdHoursPerWeek: 40,
  timeClockOvertimeMultiplier: 1.5,
  timeClockAutoCloseAfterHours: 14,
  timeClockRequireGeofence: false,
  timeClockGeofenceCenterLat: null,
  timeClockGeofenceCenterLng: null,
  timeClockGeofenceRadiusMeters: 150,
  timeClockMaxShiftHoursWarning: 10,
  timeClockBreakAutoDeductAfterHours: null,
  timeClockPtoEnabled: true,
  timeClockPtoAccrualPerHour: 0,
  timeClockPayoutMethods: ["cash", "check", "venmo", "zelle", "paypal", "other"],
  timeClockAdminNotifyOnScan: true,

  // Pay schedule defaults — biweekly is the most common small-business
  // cadence. Anchor date is the Monday of week 1 of 2026 (arbitrary;
  // admin should update on first setup).
  timeClockPayFrequency: "biweekly",
  timeClockPayWeekStart: 1, // Monday
  timeClockPayBiweeklyAnchor: "2026-01-05", // first Monday of 2026
  timeClockPayDayOffsetDays: 5, // Fri after period ends on Sun

  // Handbook (blank = no acknowledgment required)
  employeeHandbookUrl: "",
  employeeHandbookVersion: 1,

  // Coming-Soon toggles — all four customer-facing sections start as
  // coming soon while we finalise content / inventory. Admin flips
  // them off in /admin/settings → Coming Soon.
  comingSoon: {
    wigs: true,
    builder: true,
    blog: true,
    about: true,
  },

  // ─── Build lifecycle defaults ───
  buildDurationByCategory: {
    wigs: { minWeeks: 4, maxWeeks: 6 },
    "processed-wigs": { minWeeks: 2, maxWeeks: 4 },
    toppers: { minWeeks: 4, maxWeeks: 6 },
  },
  customWigDepositPercent: 50,
  buildHeartbeatDays: 14,
  balanceReminderSchedule: [7, 14, 30],
  balanceAutoRelistDays: 30,

  // Sliding refund table — earlier stages = more refund, later = less.
  // Stages match the seeded BuildStage.internalLabel values.
  customWigRefundPolicy: {
    foundation: { refundPct: 80, depositForfeited: false },
    ventilation: { refundPct: 50, depositForfeited: true },
    color: { refundPct: 25, depositForfeited: true },
    cut: { refundPct: 0, depositForfeited: true },
    wash_set: { refundPct: 0, depositForfeited: true },
    qa: { refundPct: 0, depositForfeited: true },
  },
  maxConcurrentBuilds: 0, // 0 = unlimited; admin can set a cap later
  specDriftCancelWindowHours: 48,
};

/**
 * Cross-request cached DB read (60s TTL, invalidated by
 * `revalidateTag("site-config")` from the admin write routes).
 *
 * NOTE: this function CAN throw — `getSiteConfig()` catches and falls
 * back to defaults. The throw is intentional so unstable_cache doesn't
 * cache a failure (a cached failure would pin defaults for 60s after
 * every transient DB hiccup).
 */
const getSiteConfigCached = unstable_cache(
  async (): Promise<Partial<SiteConfigData> | null> => {
    const row = await prisma.siteConfig.findUnique({
      where: { id: "singleton" },
    });
    return row ? (row.data as Partial<SiteConfigData>) : null;
  },
  ["site-config-singleton"],
  { tags: ["site-config"], revalidate: 60 }
);

/**
 * Reads SiteConfig with fallback to defaults. Deduped per React request
 * AND cached cross-request for 60s. Writers that need read-after-write
 * consistency should use getSiteConfigFresh().
 */
export const getSiteConfig = cache(async (): Promise<SiteConfigData> => {
  try {
    const stored = await getSiteConfigCached();
    if (!stored) {
      return { ...SITE_CONFIG_DEFAULTS };
    }
    return { ...SITE_CONFIG_DEFAULTS, ...stored };
  } catch {
    // DB not migrated yet or connection error — return defaults
    return { ...SITE_CONFIG_DEFAULTS };
  }
});

/**
 * Non-cached version for API routes that need fresh data after a write.
 */
export async function getSiteConfigFresh(): Promise<SiteConfigData> {
  try {
    const row = await prisma.siteConfig.findUnique({
      where: { id: "singleton" },
    });

    if (!row) {
      return { ...SITE_CONFIG_DEFAULTS };
    }

    const stored = row.data as Partial<SiteConfigData>;
    return { ...SITE_CONFIG_DEFAULTS, ...stored };
  } catch {
    return { ...SITE_CONFIG_DEFAULTS };
  }
}

/**
 * Resolve the SMS phone-number list for a notification category.
 * Reads `notificationRecipients[category]`. If empty (admin hasn't
 * customized), falls back to a sensible legacy field so we never
 * silently swallow notifications.
 *
 * Returns deduplicated, normalized (trimmed) phone strings. Empty
 * array means no recipients — caller may decide to no-op.
 */
export function resolveNotificationRecipients(
  cfg: SiteConfigData,
  category: keyof NotificationRecipientLists
): string[] {
  const list = cfg.notificationRecipients?.[category] || [];
  let resolved = list.map((p) => (p || "").trim()).filter(Boolean);
  if (resolved.length === 0) {
    let fallback = "";
    switch (category) {
      case "errorAlerts":
        fallback = cfg.errorAlertPhone || "";
        break;
      case "chatMonitor":
        fallback = cfg.chatNewConvSmsPhone || cfg.errorAlertPhone || "";
        break;
      default:
        fallback = cfg.supportPhone || "";
    }
    if (fallback) resolved = [fallback];
  }
  return Array.from(new Set(resolved));
}
